import type { ApiError } from "@/types/api";
import type { ReplaceNumberStatusResponse } from "@/services/whatsappNumberReplacementService";
import { describeFailure } from "./replacementStatusModel";

/**
 * The replacement endpoints answer 4xx/5xx with a full
 * `ReplaceNumberStatusResponse` rather than Atlas's shared error envelope, so
 * a rejection still carries a real `status` and `failureCode`. `apiClient`
 * preserves that body on {@link ApiError.body}; these helpers read it.
 */

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === "object" && !Array.isArray(value);
}

/** The replacement DTO carried by an error response, when there is one. */
export function replacementBodyOf(err: unknown): ReplaceNumberStatusResponse | null {
  const body = isRecord(err) ? (err as unknown as Partial<ApiError>).body : null;
  if (!isRecord(body)) return null;
  // `failureCode`/`stage` are unique to this DTO — enough to tell it apart
  // from the shared UiErrorResponse envelope without trusting the URL.
  if (!("failureCode" in body) && !("stage" in body) && !("replacementId" in body)) return null;
  return body as ReplaceNumberStatusResponse;
}

/** Backend `ReplacementFailureCode` for a rejection, when it reported one. */
export function failureCodeOf(err: unknown): string | null {
  const code = replacementBodyOf(err)?.failureCode;
  return typeof code === "string" && code.trim() ? code.trim() : null;
}

/**
 * User-facing message for a failed replacement call.
 *
 * Prefers the mapped `ReplacementFailureCode`, then the backend's
 * hand-authored `failureMessage` (the backend never puts raw exception text on
 * the wire), then the transport-level message. Never surfaces a stack trace or
 * an upstream response body.
 */
export function replacementErrorMessage(err: unknown, fallback: string): string {
  const body = replacementBodyOf(err);
  const mapped = describeFailure(body?.failureCode, body?.failureMessage);
  if (mapped) return mapped;

  const apiErr = isRecord(err) ? (err as unknown as Partial<ApiError>) : null;
  if (apiErr?.status === 0 || apiErr?.code === "NETWORK_ERROR") {
    return "BridgeAI could not be reached. Check your connection — this does not necessarily mean the change failed.";
  }
  const message = typeof apiErr?.message === "string" ? apiErr.message.trim() : "";
  // Axios' own "Request failed with status code 409" is noise, not an answer.
  if (message && !/^request failed with status code/i.test(message)) return message;
  return fallback;
}

/** True when Atlas rejected the call because a replacement already holds the channel lock. */
export function isAlreadyInProgress(err: unknown): boolean {
  return failureCodeOf(err) === "REPLACEMENT_ALREADY_IN_PROGRESS";
}

/**
 * True when the request never reached Atlas (or its answer never got back).
 *
 * The distinction matters: on a transport failure the replacement may well
 * have been created, so the caller must re-read the status endpoint rather
 * than reporting failure or letting the operator submit again.
 */
export function isTransportFailure(err: unknown): boolean {
  const apiErr = isRecord(err) ? (err as unknown as Partial<ApiError>) : null;
  if (!apiErr) return false;
  return apiErr.status === 0 || (apiErr.code === "NETWORK_ERROR" && !replacementBodyOf(err));
}
